import DOMPurify from 'dompurify'

const ALLOWED_TAGS = [
  'style',
  'div',
  'p',
  'img',
  'a',
  'span',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'hr',
  'b',
  'i',
  'strong',
  'em',
  'br',
  'details',
  'summary',
  'label',
  'input',
  'figure',
  'figcaption',
  'blockquote',
  'cite',
  'section',
  'article',
]

const ALLOWED_ATTR = [
  'href',
  'src',
  'alt',
  'title',
  'target',
  'rel',
  'class',
  'id',
  'style',
  'type',
  'for',
  'name',
  'value',
  'checked',
  'width',
  'height',
  'srcset',
  'sizes',
  'loading',
  'aria-label',
  'aria-hidden',
]

const FORBID_TAGS = ['script', 'iframe', 'object', 'embed', 'form']

function stripBlockedTags(html: string): string {
  return FORBID_TAGS.reduce((safeHtml, tag) => {
    const pairTagRegex = new RegExp(
      `<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}>`,
      'gi'
    )

    const selfClosingTagRegex = new RegExp(`<${tag}\\b[^>]*\\/?\\s*>`, 'gi')

    return safeHtml.replace(pairTagRegex, '').replace(selfClosingTagRegex, '')
  }, html)
}

function stripInlineEventHandlers(html: string): string {
  return html
    .replace(/\son[a-z0-9_-]+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son[a-z0-9_-]+\s*=\s*'[^']*'/gi, '')
    .replace(/\son[a-z0-9_-]+\s*=\s*[^\s>]+/gi, '')
}

function stripUnsafeProtocols(html: string): string {
  return html.replace(
    /\s(?:href|src)\s*=\s*(["'])\s*(?:javascript:|data:text\/html)[^"']*\1/gi,
    ''
  )
}

function enforceSafeBlankTargets(html: string): string {
  return html.replace(/<a\b([^>]*)>/gi, (fullMatch, attributes) => {
    if (!/target\s*=\s*(["'])_blank\1/i.test(attributes)) {
      return fullMatch
    }

    if (/rel\s*=\s*(["'])[^"']*\1/i.test(attributes)) {
      return fullMatch.replace(
        /rel\s*=\s*(["'])([^"']*)\1/i,
        (_relMatch, quote, relValue) => {
          const normalized = relValue.toLowerCase()
          const nextRel = [
            ...new Set([
              ...normalized.split(/\s+/).filter(Boolean),
              'noopener',
              'noreferrer',
            ]),
          ].join(' ')

          return `rel=${quote}${nextRel}${quote}`
        }
      )
    }

    return `<a${attributes} rel="noopener noreferrer">`
  })
}

export function sanitizeProductCustomHtml(
  htmlString?: string | null,
  moreTags: string[] = [],
  moreAttrs: string[] = []
): string {
  if (!htmlString || htmlString.trim().length === 0) {
    return ''
  }

  const strippedHtml = stripBlockedTags(htmlString)
  const strippedEventHandlersHtml = stripInlineEventHandlers(strippedHtml)

  if (typeof DOMPurify?.sanitize !== 'function') {
    // Avoid rendering partially-sanitized HTML when DOMPurify isn't available (e.g. SSR).
    return ''
  }

  const sanitized = DOMPurify.sanitize(strippedEventHandlersHtml, {
    FORCE_BODY: true,
    ALLOWED_TAGS: [...ALLOWED_TAGS, ...moreTags],
    ALLOWED_ATTR: [...ALLOWED_ATTR, ...moreAttrs],
    FORBID_TAGS,
    ALLOW_DATA_ATTR: true,
  })

  return enforceSafeBlankTargets(stripUnsafeProtocols(String(sanitized))).trim()
}
